import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { PaymentCommandService } from './payment-command.service';
import { PaymentQueryService } from './payment-query.service';
import {
  ClearPaymentState,
  ProceedCustomPayment,
  ProceedCustomPaymentFailure,
  ProceedCustomPaymentSuccess,
  SearchBill,
  SearchBillFailure,
  SearchBillSuccess,
} from './payment.actions';
import { PaymentStateModel } from './payment.model';

@State<PaymentStateModel>({
  name: 'payment',
  defaults: {
    loading: false,
    error: null,
    result: null,
    paymentReceipt: null,
    paymentError: null,
  },
})
@Injectable()
export class PaymentState {
  constructor(
    private paymentQuery: PaymentQueryService,
    private paymentCommand: PaymentCommandService,
    private translate: TranslateService
  ) {}

  @Selector()
  static loading(state: PaymentStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static result(state: PaymentStateModel) {
    return state.result;
  }

  @Selector()
  static error(state: PaymentStateModel): string | null {
    return state.error;
  }

  @Selector()
  static paymentReceipt(state: PaymentStateModel) {
    return state.paymentReceipt;
  }

  @Selector()
  static paymentError(state: PaymentStateModel): string | null {
    return state.paymentError;
  }

  @Action(SearchBill)
  searchBill(ctx: StateContext<PaymentStateModel>, action: SearchBill) {
    ctx.patchState({ loading: true, error: null });

    this.paymentQuery
      .searchBill(action.payload.billNumber, action.payload.payerName, action.payload.amount)
      .subscribe({
        next: (result) => {
          if (result) {
            ctx.dispatch(new SearchBillSuccess(result));
          } else {
            ctx.dispatch(
              new SearchBillFailure(this.translate.instant('home.paymentForm.notFoundMessage'))
            );
          }
        },
        error: (error) => {
          ctx.dispatch(new SearchBillFailure(this.resolveSearchBillErrorMessage(error)));
        },
      });
  }

  @Action(SearchBillSuccess)
  searchBillSuccess(ctx: StateContext<PaymentStateModel>, action: SearchBillSuccess) {
    ctx.patchState({
      loading: false,
      result: action.payload,
      error: null,
    });
  }

  @Action(SearchBillFailure)
  searchBillFailure(ctx: StateContext<PaymentStateModel>, action: SearchBillFailure) {
    ctx.patchState({
      loading: false,
      error: action.payload,
      result: null,
    });
  }

  @Action(ProceedCustomPayment)
  proceedCustomPayment(ctx: StateContext<PaymentStateModel>, action: ProceedCustomPayment) {
    ctx.patchState({ paymentError: null });

    this.paymentCommand.proceedCustomPayment(action.payload).subscribe({
      next: (result) => {
        ctx.dispatch(new ProceedCustomPaymentSuccess(result));
      },
      error: (error) => {
        ctx.dispatch(new ProceedCustomPaymentFailure(error));
      },
    });
  }

  @Action(ProceedCustomPaymentSuccess)
  proceedCustomPaymentSuccess(ctx: StateContext<PaymentStateModel>, action: ProceedCustomPaymentSuccess) {
    ctx.patchState({
      paymentReceipt: action.payload,
      paymentError: null,
    });
  }

  @Action(ProceedCustomPaymentFailure)
  proceedCustomPaymentFailure(ctx: StateContext<PaymentStateModel>, action: ProceedCustomPaymentFailure) {
    let message = '';

    if (action.payload && typeof action.payload === 'object' && 'status' in action.payload) {
      const error = action.payload as any;
      const errorStatus: string = this.extractErrorText(error);

      if (error.status === 404) {
        message = this.translate.instant('memberBills.invoicePayment.errors.noBillsFound');
      } else if (this.isInscriptionBillNotPayableError(error)) {
        message = this.translate.instant('memberBills.invoicePayment.errors.inscriptionBillNotPayable');
      } else if (error.status === 400) {
        message = this.translate.instant('memberBills.invoicePayment.errors.sumOrMonerisError');
      } else if (errorStatus && errorStatus.toUpperCase().indexOf('DECLINED') >= 0) {
        message = this.translate.instant('memberBills.invoicePayment.errors.transactionError');
      } else {
        message = this.translate.instant('memberBills.invoicePayment.errors.paymentError');
      }
    } else {
      const errorText = action.payload || '';

      if (
        typeof errorText === 'string' &&
        (errorText.includes('Http failure response for') ||
          errorText.includes('500 Internal Server Error') ||
          errorText.includes('Internal Server Error') ||
          errorText.includes('Online payment is not yet supported'))
      ) {
        message = this.translate.instant('memberBills.invoicePayment.errors.paymentError');
      } else {
        message = errorText || this.translate.instant('memberBills.invoicePayment.errors.paymentError');
      }
    }

    ctx.patchState({
      paymentError: message,
      paymentReceipt: null,
    });
  }

  private resolveSearchBillErrorMessage(error: any): string {
    if (this.isInscriptionBillNotPayableError(error)) {
      return this.translate.instant('memberBills.invoicePayment.errors.inscriptionBillNotPayable');
    }

    if (error?.status === 404) {
      return this.translate.instant('home.paymentForm.notFoundMessage');
    }

    const errorText = this.extractErrorText(error);
    return errorText || this.translate.instant('memberBills.invoicePayment.errors.paymentError');
  }

  private isInscriptionBillNotPayableError(error: any): boolean {
    const errorText = this.extractErrorText(error);
    return errorText === 'INSCRIPTION_BILL_NOT_PAYABLE'
      || errorText.includes('INSCRIPTION_BILL_NOT_PAYABLE');
  }

  private extractErrorText(error: any): string {
    if (!error) {
      return '';
    }

    if (typeof error === 'string') {
      return error;
    }

    if (typeof error.error === 'string') {
      return error.error;
    }

    if (error.error && typeof error.error === 'object') {
      const problemDetails = error.error as Record<string, unknown>;
      const detail = problemDetails.detail ?? problemDetails.Detail;
      if (typeof detail === 'string' && detail) {
        return detail;
      }

      const message = problemDetails.message ?? problemDetails.Message;
      if (typeof message === 'string' && message) {
        return message;
      }
    }

    if (typeof error.message === 'string' && !error.message.startsWith('Http failure response for')) {
      return error.message;
    }

    return '';
  }

  @Action(ClearPaymentState)
  clearPaymentState(ctx: StateContext<PaymentStateModel>) {
    ctx.patchState({
      paymentReceipt: null,
      paymentError: null,
    });
  }
}
